import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  getEmployees,
  getLocations,
  getJobPositions,
  getRoles,
  createEmployee,
  updateEmployee,
  deactivateEmployee,
  reactivateEmployee,
} from "../../../../utils/services/control/masterDataService";
import {
  createUser,
  assignRole,
} from "../../../../utils/services/control/userManagementService";
import EmployeesDataGrid from "./EmployeesDataGrid";
import EmployeeFormDialog from "./EmployeeFormDialog";
import EmployeeDetailDialog from "./EmployeeDetailDialog";
import CreateUserDialog from "./CreateUserDialog";
import ConfirmDialog from "../common/ConfirmDialog";
import ExportExcelButton from "../../../../components/Basic/ExportExcelButton";
import { formatDate } from "../../../../utils/formatters/date";
import useSnack from "../../hooks/useSnack";

const EMPLOYEE_EXPORT_COLUMNS = [
  { field: "employeeId", headerName: "Employee ID" },
  { field: "firstName", headerName: "First Name" },
  { field: "lastName", headerName: "Last Name" },
  { field: "mail", headerName: "Email" },
  { field: "businessEmail", headerName: "Business Email" },
  { field: "jobPositionName", headerName: "Job Position" },
  {
    field: "admission",
    headerName: "Admission Date",
    valueGetter: (r) => formatDate(r.admission),
  },
  { field: "activeLocations", headerName: "Active Locations" },
  {
    field: "hasUser",
    headerName: "Has User Account",
    valueGetter: (r) => (r.hasUser ? "Yes" : "No"),
  },
  {
    field: "status",
    headerName: "Status",
    valueGetter: (r) => (r.status === 1 ? "Active" : "Inactive"),
  },
];

const EmployeesTab = () => {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(1);
  const [filterLocation, setFilterLocation] = useState("");
  const [filterJobPosition, setFilterJobPosition] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [createUserTarget, setCreateUserTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const { snack, showSnack, closeSnack } = useSnack();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus !== "") filters.status = filterStatus;
      if (filterLocation) filters.locationId = filterLocation;
      if (filterJobPosition) filters.jobPositionId = filterJobPosition;
      if (search.trim()) filters.search = search.trim();

      const [empData, locData, jpData, rolesData] = await Promise.all([
        getEmployees(filters),
        getLocations(),
        getJobPositions(),
        getRoles(),
      ]);
      setEmployees(empData);
      setLocations(locData);
      setJobPositions(jpData);
      setRoles(rolesData);
    } catch (err) {
      console.error("Error loading employees:", err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterLocation, filterJobPosition, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateOpen = () => {
    setEditEmployee(null);
    setFormOpen(true);
  };

  const handleEditOpen = (emp) => {
    setEditEmployee(emp);
    setFormOpen(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const isEdit = Boolean(data.employeeId);
      const res = isEdit
        ? await updateEmployee(data)
        : await createEmployee(data);

      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          isEdit
            ? "Employee updated successfully"
            : "Employee created successfully"
        );
        setFormOpen(false);
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error saving employee",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setSaving(true);
    try {
      const res = await deactivateEmployee(deactivateTarget.employeeId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack("Employee deactivated successfully");
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error deactivating employee",
        "error"
      );
    } finally {
      setSaving(false);
      setDeactivateTarget(null);
    }
  };

  const handleReactivate = async () => {
    setSaving(true);
    try {
      const res = await reactivateEmployee(reactivateTarget.employeeId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack("Employee reactivated successfully");
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error reactivating employee",
        "error"
      );
    } finally {
      setSaving(false);
      setReactivateTarget(null);
    }
  };

  const handleCreateUser = async (data) => {
    setSaving(true);
    try {
      const { roleIds, ...userData } = data;
      const res = await createUser(userData);
      if (res.result !== 0) {
        showSnack(res.message, "error");
        return;
      }
      if (roleIds?.length) {
        for (const roleId of roleIds) {
          await assignRole({ employeeId: data.employeeId, roleId });
        }
      }
      showSnack("User account created and roles assigned");
      setCreateUserTarget(null);
      loadData();
    } catch (err) {
      showSnack(err.response?.data?.message || "Error creating user", "error");
    } finally {
      setSaving(false);
    }
  };

  const targetName = (e) => `${e?.firstName || ""} ${e?.lastName || ""}`.trim();

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={filterLocation}
            label="Location"
            onChange={(e) => setFilterLocation(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {locations
              .filter((l) => l.status === 1)
              .map((l) => (
                <MenuItem key={l.locationId} value={l.locationId}>
                  {l.medicalCenter}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Job Position</InputLabel>
          <Select
            value={filterJobPosition}
            label="Job Position"
            onChange={(e) => setFilterJobPosition(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {jobPositions.map((jp) => (
              <MenuItem key={jp.jobPositionId} value={jp.jobPositionId}>
                {jp.jobPositionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <ExportExcelButton
          rows={employees}
          columns={EMPLOYEE_EXPORT_COLUMNS}
          filename="employees"
        />
        <Button
          variant="contained"
          startIcon={<PersonAddOutlinedIcon />}
          onClick={handleCreateOpen}
          size="small"
        >
          New Employee
        </Button>
      </Box>

      <EmployeesDataGrid
        rows={employees}
        loading={loading}
        onEdit={handleEditOpen}
        onDetail={(emp) => setDetailEmployee(emp)}
        onDeactivate={(emp) => setDeactivateTarget(emp)}
        onReactivate={(emp) => setReactivateTarget(emp)}
        onCreateUser={(emp) => setCreateUserTarget(emp)}
      />

      <EmployeeFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        employee={editEmployee}
        locations={locations}
        jobPositions={jobPositions}
        employees={employees}
        loading={saving}
      />

      <EmployeeDetailDialog
        open={Boolean(detailEmployee)}
        onClose={() => setDetailEmployee(null)}
        employee={detailEmployee}
        allLocations={locations}
        onUserChanged={loadData}
      />

      <CreateUserDialog
        open={Boolean(createUserTarget)}
        onClose={() => setCreateUserTarget(null)}
        onSave={handleCreateUser}
        employee={createUserTarget}
        roles={roles}
        loading={saving}
      />

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate Employee"
        message={`Deactivate ${targetName(deactivateTarget)}? This will also deactivate their user account, sessions, roles, locations, and PCP configs.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateTarget(null)}
        loading={saving}
      />

      <ConfirmDialog
        open={Boolean(reactivateTarget)}
        title="Reactivate Employee"
        message={`Reactivate ${targetName(reactivateTarget)}? Only the employee record will be reactivated — their user account, roles, locations, and PCP config will NOT be restored automatically and must be reassigned manually.`}
        confirmLabel="Reactivate"
        confirmColor="success"
        onConfirm={handleReactivate}
        onCancel={() => setReactivateTarget(null)}
        loading={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeesTab;
